import { apiBaseUrl, normalizeUrl } from "@/shared/lib/urls";

export const MCP_ENDPOINT_STORAGE_KEY = "nodric.mcp.endpoint";
export const MCP_SERVER_NAME = "nodric";

export { apiBaseUrl };

/**
 * Normaliza un endpoint MCP para que sea usable por un cliente externo (Claude Code):
 * absolutiza rutas relativas ("/mcp" → "https://<host-actual>/mcp"), colapsa `//` en el
 * path y recorta la barra final.
 */
export function resolveMcpEndpoint(raw: string): string {
  const value = raw.trim();
  if (!value) return value;
  const absolute = value.startsWith("/") ? `${window.location.origin}${value}` : value;
  return normalizeUrl(absolute).replace(/\/+$/, "");
}

/** Endpoint MCP por defecto: VITE_MCP_URL, o derivado del host del backend en el puerto 5199. */
export function defaultMcpEndpoint(): string {
  const fromEnv = import.meta.env.VITE_MCP_URL as string | undefined;
  if (fromEnv && fromEnv.trim()) return resolveMcpEndpoint(fromEnv);
  try {
    const api = new URL(apiBaseUrl());
    return `${api.protocol}//${api.hostname}:5199/mcp`;
  } catch {
    return "http://localhost:5199/mcp";
  }
}

/** Cabecera Authorization a partir del token; placeholder si aún no se generó. */
function authHeaderValue(token: string): string {
  const v = token.trim();
  return `Bearer ${v || "<tu-token-nod_pat_...>"}`;
}

interface ConfigInput {
  endpoint: string;
  token: string;
  serverName?: string;
}

/** `.mcp.json` para Claude Code (transporte HTTP nativo). */
export function buildClaudeCodeConfig({
  endpoint,
  token,
  serverName = MCP_SERVER_NAME,
}: ConfigInput): string {
  return JSON.stringify(
    {
      mcpServers: {
        [serverName]: {
          type: "http",
          url: endpoint,
          headers: { Authorization: authHeaderValue(token) },
        },
      },
    },
    null,
    2,
  );
}

/** `claude_desktop_config.json` — Claude Desktop no habla HTTP nativo, usa el puente `mcp-remote`. */
export function buildClaudeDesktopConfig({
  endpoint,
  token,
  serverName = MCP_SERVER_NAME,
}: ConfigInput): string {
  return JSON.stringify(
    {
      mcpServers: {
        [serverName]: {
          command: "npx",
          args: ["mcp-remote", endpoint, "--header", `Authorization:${authHeaderValue(token)}`],
        },
      },
    },
    null,
    2,
  );
}

/** curl de smoke test contra el endpoint MCP (handshake initialize). */
export function buildSmokeTestCurl({ endpoint, token }: ConfigInput): string {
  return [
    `curl -s -X POST "${endpoint}" \\`,
    `  -H "Content-Type: application/json" \\`,
    `  -H "Accept: application/json, text/event-stream" \\`,
    `  -H "Authorization: ${authHeaderValue(token)}" \\`,
    `  -d '{"jsonrpc":"2.0","id":1,"method":"initialize","params":{"protocolVersion":"2025-11-25","capabilities":{},"clientInfo":{"name":"nodric-front","version":"1"}}}'`,
  ].join("\n");
}
